import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  FileText, Users, Bell, Building2, Shield, ClipboardCheck,
  Lock, CheckSquare, BarChart3, TrendingUp, ArrowRight, Target,
  Check
} from "lucide-react";
import { Button } from "../components/ui/button";
import DemoRequestModal from "../components/DemoRequestModal";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const mainFeatures = [
  {
    icon: Users,
    title: "Provider Management",
    subtitle: "Keep all provider information in one place.",
    details: [
      "Centralized provider profiles",
      "License, DEA, malpractice tracking",
      "Expiration alerts",
      "Document storage per provider"
    ]
  },
  {
    icon: Bell,
    title: "Credentialing",
    subtitle: "Simplify and accelerate credentialing workflows.",
    details: [
      "CAQH integration",
      "Payer enrollment tracking",
      "Automated reminders",
      "Credential lifecycle management"
    ]
  },
  {
    icon: FileText,
    title: "Documents & E-Sign",
    subtitle: "Eliminate document chaos.",
    details: [
      "Secure, encrypted storage",
      "AI-powered classification",
      "Built-in e-signatures",
      "Version control"
    ]
  },
  {
    icon: Target,
    title: "Task & Workflow Engine",
    subtitle: "Run your operations with clarity and structure.",
    details: [
      "Kanban, calendar, and timeline views",
      "Task automation and reminders",
      "Team collaboration"
    ]
  },
  {
    icon: BarChart3,
    title: "Analytics & Reporting",
    subtitle: "Make better decisions with real-time insights.",
    details: [
      "AR aging reports",
      "Denial tracking",
      "Financial dashboards"
    ]
  }
];

const additionalFeatures = [
  {
    icon: ClipboardCheck,
    title: "Payer Enrollments",
    description: "Track and manage all payer enrollments in one place."
  },
  {
    icon: Shield,
    title: "HIPAA Compliance",
    description: "Built-in security with encryption and full audit logs."
  },
  {
    icon: TrendingUp,
    title: "Claims Tracking",
    description: "Monitor claims and denials with detailed analytics."
  },
  {
    icon: Lock,
    title: "Role-Based Access",
    description: "Control who sees what with granular permissions."
  }
];

export default function Features() {
  const [demoModalOpen, setDemoModalOpen] = useState(false);

  return (
    <>
      <DemoRequestModal isOpen={demoModalOpen} onClose={() => setDemoModalOpen(false)} />

      {/* Hero */}
      <section className="pt-28 pb-8 sm:pt-32 sm:pb-10 md:pt-36 md:pb-12 px-4 sm:px-6" data-testid="features-hero">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.p 
              variants={fadeInUp}
              className="text-xs tracking-[0.15em] uppercase font-bold text-[#8A9A86] mb-3"
            >
              Features
            </motion.p>
            <motion.h1 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-[#1A362D] font-medium mb-4"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Everything You Need to<br />Run Your Operations
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              className="text-base sm:text-lg text-[#5C6C65] max-w-xl mx-auto"
            >
              Praqio brings together all the tools RCM teams and medical practices need in one powerful platform.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Main Features Grid */}
      <section className="py-8 sm:py-10 md:py-12 px-4 sm:px-6" data-testid="main-features">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            {mainFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 sm:p-8 border border-[#E2E8D8] hover:shadow-lg transition-shadow"
                data-testid={`feature-${index}`}
              >
                <div className="w-12 h-12 rounded-xl bg-[#1A362D] flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 
                  className="text-xl font-medium text-[#1A362D] mb-2"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  {feature.title}
                </h3>
                <p className="text-[#C87961] text-sm font-medium mb-4">
                  {feature.subtitle}
                </p>
                <ul className="space-y-2">
                  {feature.details.map((detail, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <Check size={14} className="text-[#8A9A86] flex-shrink-0" />
                      <span className="text-sm text-[#5C6C65]">{detail}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Features */}
      <section className="py-8 sm:py-10 md:py-12 px-4 sm:px-6 bg-[#F9F6F0]" data-testid="additional-features">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-8"
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-2xl sm:text-3xl tracking-tight text-[#1A362D] font-medium"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Plus More Capabilities
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4"
          >
            {additionalFeatures.map((feature, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-white rounded-xl p-5 border border-[#E2E8D8]"
              >
                <div className="w-9 h-9 rounded-lg bg-[#E2E8D8] flex items-center justify-center mb-3">
                  <feature.icon className="w-4 h-4 text-[#1A362D]" />
                </div>
                <h3 className="text-sm font-medium text-[#1A362D] mb-1">
                  {feature.title}
                </h3>
                <p className="text-xs text-[#5C6C65]">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 sm:py-12 md:py-14 px-4 sm:px-6 bg-white">
        <div className="max-w-2xl mx-auto text-center">
          <h2 
            className="text-2xl sm:text-3xl tracking-tight text-[#1A362D] font-medium mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Ready to See Praqio in Action?
          </h2>
          <p className="text-[#5C6C65] mb-6">
            Book a demo and see how Praqio can transform your operations.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button 
              className="btn-pill btn-accent px-6 py-5"
              onClick={() => setDemoModalOpen(true)}
            >
              Book a Demo
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Link to="/pricing">
              <Button variant="outline" className="btn-pill btn-outline px-6 py-5 w-full">
                View Pricing
              </Button>
            </Link>
          </div>
          <p className="text-xs text-[#8A9A86] mt-6">
            A product of <span className="text-[#1A362D] font-medium">Capital Billing Services, Inc.</span>
          </p>
        </div>
      </section>
    </>
  );
}
